import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Loader2, Send, AlertTriangle } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import { Pagination } from '@/components/Pagination'
import { HelpTooltip } from '@/components/HelpTooltip'
import { cn } from '@/lib/cn'

const PAGE_SIZE = 20

/**
 * One row in the selector modal. `id` keys the selection state and is
 * what gets sent to /api/reporting/post-selected. `blocked` rows render
 * dimmed and can't be ticked - their period is closed or sits behind
 * the TB-import cutoff and the backend would refuse them anyway. The
 * `blockedReason` line tells the user why.
 *
 * Two render shapes are supported:
 *  - **Table mode** (`tab.columns` is set, `cells` provided): rows render
 *    as a real `<table>` with one `<td>` per column, matching the old
 *    UnpostedPreviewModal's denser presentation.
 *  - **Stacked mode** (no columns): falls back to the simple list with
 *    `primary` on top and `secondary` underneath. Kept for backward
 *    compatibility with any call sites that don't define columns.
 */
export interface PostingSelectorItem {
  id: number
  /** Stacked-mode headline (description + amount on one line). */
  primary?: ReactNode
  /** Stacked-mode subtitle (date · accounts · category). */
  secondary?: ReactNode
  /** Table-mode cells, one per column on the parent tab. */
  cells?: ReactNode[]
  blocked?: boolean
  blockedReason?: string
}

export interface PostingSelectorColumn {
  header: string
  /** Default left. Use 'right' for monetary / numeric columns. */
  align?: 'left' | 'right' | 'center'
  /** Optional fixed width (Tailwind class, e.g. 'w-32'). */
  width?: string
}

export interface PostingSelectorTab {
  /** Stable key used for the tab id. */
  key: string
  /** Human-readable label, e.g. "Accruals". */
  label: string
  items: PostingSelectorItem[]
  /** Empty-state copy for this tab when no rows are pending. */
  emptyMessage?: string
  /**
   * If supplied, the tab renders as a table with these column headers.
   * Each item must provide a matching `cells` array. A leading checkbox
   * column is auto-added when the modal is not in readOnly mode, and a
   * trailing "Status" column shows Ready/Blocked badges.
   */
  columns?: PostingSelectorColumn[]
}

export interface PostingSelectorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  loading?: boolean
  /** Multi-tab variant for the Reporting page. Single-source pages pass one tab. */
  tabs: PostingSelectorTab[]
  /** True while the post mutation is running. */
  posting?: boolean
  /** Receives a map { [tabKey]: number[] }. Caller fans out per source. */
  onPost: (selection: Record<string, number[]>) => void | Promise<void>
  /** Read-only mode: hide the Post button, only show "Close". Used by "View pending". */
  readOnly?: boolean
  /**
   * Optional handler for the read-only "Post Now" affordance. When supplied,
   * a Post Now button is shown next to Close in read-only mode so the user
   * can flip to the selectable view without closing and re-opening the
   * modal. Typical caller wiring: `() => setSelectorReadOnly(false)`.
   */
  onSwitchToPost?: () => void
}

/**
 * Picker modal used by every page's "Post Now" banner. Renders a list
 * of unposted entries with per-row checkboxes, a "Select all / Clear"
 * toggle, and (when more than one source is provided) a tab strip plus
 * a "global select all" affordance for the whole modal - that's the
 * Financial Reports flow. Single-source callers (Accruals, JE, etc.)
 * pass `tabs.length === 1` and the tab strip is hidden.
 *
 * Crucially, posting one tab's selection NEVER drags rows from another
 * tab into the request. The submit handler sends one /api/reporting/
 * post-selected request per tab that has at least one ticked row.
 * That's the contract the user asked for ("posting of one should not
 * reflect the others to post").
 */
export function PostingSelectorModal({
  open,
  onOpenChange,
  title,
  description,
  loading,
  tabs,
  posting,
  onPost,
  readOnly,
  onSwitchToPost,
}: PostingSelectorModalProps) {
  // Selection is { [tabKey]: Set<id> }. Reset whenever the modal opens
  // so a previous "abandoned" selection from another tenant / source
  // doesn't leak into the current view.
  const [selection, setSelection] = useState<Record<string, Set<number>>>({})
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.key ?? '')
  // Per-tab pagination state. 0-indexed page numbers. Reset on open
  // and whenever the user switches tabs (so they always land on page 1
  // of the new tab) - handled by setActiveTab below.
  const [pageByTab, setPageByTab] = useState<Record<string, number>>({})

  useEffect(() => {
    if (!open) return
    const init: Record<string, Set<number>> = {}
    const pages: Record<string, number> = {}
    for (const t of tabs) {
      init[t.key] = new Set()
      pages[t.key] = 0
    }
    setSelection(init)
    setPageByTab(pages)
    setActiveTab(tabs[0]?.key ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Why: if the visible tab's row count shrinks (e.g. after a successful
  // post), an out-of-range page would show an empty body. Clamp every
  // tab's page index to the new total whenever items change.
  useEffect(() => {
    setPageByTab((prev) => {
      const next = { ...prev }
      let changed = false
      for (const t of tabs) {
        const lastPage = Math.max(0, Math.ceil(t.items.length / PAGE_SIZE) - 1)
        if ((next[t.key] ?? 0) > lastPage) {
          next[t.key] = lastPage
          changed = true
        }
        if (next[t.key] === undefined) {
          next[t.key] = 0
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [tabs])

  const isSingle = tabs.length <= 1

  const toggleRow = (tabKey: string, id: number) => {
    setSelection((prev) => {
      const next = { ...prev, [tabKey]: new Set(prev[tabKey] ?? []) }
      if (next[tabKey].has(id)) next[tabKey].delete(id)
      else next[tabKey].add(id)
      return next
    })
  }

  const selectableIdsFor = (tabKey: string) => {
    const tab = tabs.find((t) => t.key === tabKey)
    if (!tab) return [] as number[]
    return tab.items.filter((i) => !i.blocked).map((i) => i.id)
  }

  const setTabSelection = (tabKey: string, ids: number[]) => {
    setSelection((prev) => ({ ...prev, [tabKey]: new Set(ids) }))
  }

  const totalSelected = useMemo(
    () => Object.values(selection).reduce((sum, s) => sum + s.size, 0),
    [selection],
  )

  const totalSelectable = useMemo(
    () => tabs.reduce((sum, t) => sum + t.items.filter((i) => !i.blocked).length, 0),
    [tabs],
  )

  const allSelected = totalSelectable > 0 && totalSelected >= totalSelectable

  const selectAllGlobal = () => {
    const next: Record<string, Set<number>> = {}
    for (const t of tabs) {
      next[t.key] = new Set(t.items.filter((i) => !i.blocked).map((i) => i.id))
    }
    setSelection(next)
  }

  const clearAllGlobal = () => {
    const next: Record<string, Set<number>> = {}
    for (const t of tabs) next[t.key] = new Set()
    setSelection(next)
  }

  const submit = async () => {
    const payload: Record<string, number[]> = {}
    for (const t of tabs) {
      const ids = Array.from(selection[t.key] ?? [])
      if (ids.length > 0) payload[t.key] = ids
    }
    await onPost(payload)
  }

  const renderTabContent = (tab: PostingSelectorTab) => {
    const selected = selection[tab.key] ?? new Set<number>()
    const selectable = selectableIdsFor(tab.key)
    // Tri-state: 'all' / 'some' / 'none'. Drives the master checkbox
    // (matches the permissions-picker pattern in Team Management).
    const tabSelectState: 'all' | 'some' | 'none' =
      selectable.length === 0
        ? 'none'
        : selected.size >= selectable.length
          ? 'all'
          : selected.size === 0
            ? 'none'
            : 'some'
    const blockedCount = tab.items.filter((i) => i.blocked).length
    // Why: paginate at PAGE_SIZE so a tab with hundreds of pending rows
    // stays scannable. Selection state spans every page (you can tick
    // page 1 rows, flip to page 2, tick more, then post - all selected
    // ids are submitted together) because `selection` is keyed by id,
    // not by page index.
    const page = pageByTab[tab.key] ?? 0
    const pageStart = page * PAGE_SIZE
    const visibleItems = tab.items.slice(pageStart, pageStart + PAGE_SIZE)
    const renderTab: PostingSelectorTab = { ...tab, items: visibleItems }
    return (
      // Why: pt-2 puts breathing room between the tab strip and the
      // header row; space-y-4 (was space-y-3) widens the gap between
      // the meta row, the table, and the pagination so the modal
      // doesn't feel cramped.
      <div className="space-y-4 pt-2">
        <div className="flex flex-wrap items-center justify-between gap-3 px-1 text-xs text-muted-foreground">
          <span>
            {readOnly
              ? `${tab.items.length} pending${blockedCount ? ` · ${blockedCount} blocked` : ''}`
              : `${selected.size} of ${selectable.length} selected${blockedCount ? ` · ${blockedCount} blocked` : ''}`}
          </span>
          {!readOnly ? (
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-transparent px-2 py-1 text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground">
              <Checkbox
                checked={
                  tabSelectState === 'all' ? true
                  : tabSelectState === 'some' ? 'indeterminate'
                  : false
                }
                onCheckedChange={() =>
                  setTabSelection(tab.key, tabSelectState === 'all' ? [] : selectable)
                }
                disabled={selectable.length === 0}
                aria-label={`Select all ${tab.label}`}
              />
              <span className="font-medium">Select all</span>
            </label>
          ) : null}
        </div>
        {tab.items.length === 0 ? (
          <EmptyState
            title={readOnly ? 'Nothing pending' : 'Nothing to post'}
            description={tab.emptyMessage ?? 'There are no pending entries on this source.'}
          />
        ) : (
          <>
            {renderTab.columns ? (
              // Table mode - denser presentation that matches the previous
              // UnpostedPreviewModal. Adds a leading checkbox column when
              // not readOnly. The auto Status column is hidden unless this
              // tab has at least one blocked row, since "Ready" on every
              // row is just noise and collides with domain status columns
              // (Invoice status, Bill status) the call site provides.
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {!readOnly ? <th className="w-10 px-3 py-2 text-left"></th> : null}
                      {renderTab.columns.map((col, i) => (
                        <th
                          key={i}
                          className={cn(
                            'px-3 py-2',
                            col.align === 'right' && 'text-right',
                            col.align === 'center' && 'text-center',
                            (col.align === 'left' || !col.align) && 'text-left',
                            col.width,
                          )}
                        >
                          {col.header}
                        </th>
                      ))}
                      {blockedCount > 0 ? (
                        <th className="w-10 px-2 py-2 text-center" aria-label="Blocked indicator" />
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {renderTab.items.map((item) => {
                      const checked = selected.has(item.id)
                      const disabled = !!item.blocked
                      return (
                        <tr
                          key={item.id}
                          className={cn(
                            'border-t hover:bg-muted/30',
                            item.blocked ? 'opacity-70' : null,
                          )}
                        >
                          {!readOnly ? (
                            <td className="px-3 py-2 align-middle">
                              <Checkbox
                                checked={checked}
                                disabled={disabled}
                                onCheckedChange={() => toggleRow(tab.key, item.id)}
                                aria-label={`Select row ${item.id}`}
                              />
                            </td>
                          ) : null}
                          {(item.cells ?? []).map((cell, i) => (
                            <td
                              key={i}
                              className={cn(
                                'px-3 py-2 align-middle',
                                renderTab.columns?.[i]?.align === 'right' && 'text-right font-mono text-xs',
                                renderTab.columns?.[i]?.align === 'center' && 'text-center',
                              )}
                            >
                              {cell}
                            </td>
                          ))}
                          {blockedCount > 0 ? (
                            <td className="px-2 py-2 text-center align-middle">
                              {item.blocked ? (
                                <span
                                  className="inline-flex"
                                  title={item.blockedReason ?? 'Blocked'}
                                >
                                  <AlertTriangle className="size-4 text-warning" aria-hidden />
                                </span>
                              ) : null}
                            </td>
                          ) : null}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <ul className="divide-y rounded-md border">
                {renderTab.items.map((item) => {
                  const checked = selected.has(item.id)
                  const disabled = !!item.blocked
                  return (
                    <li
                      key={item.id}
                      className={cn(
                        'flex items-start gap-3 px-3 py-2',
                        item.blocked ? 'opacity-60' : null,
                      )}
                    >
                      {/* Why: in read-only "View pending" mode the checkboxes
                          are completely hidden - the list is for review only,
                          not selection. Keeping disabled checkboxes around
                          made the View and Post Now modals look identical. */}
                      {!readOnly ? (
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={() => toggleRow(tab.key, item.id)}
                          className="mt-1"
                          aria-label={`Select row ${item.id}`}
                        />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm">{item.primary}</div>
                        {item.secondary ? (
                          <div className="text-xs text-muted-foreground">{item.secondary}</div>
                        ) : null}
                        {item.blocked && item.blockedReason ? (
                          <div className="mt-0.5 text-xs text-warning">
                            Blocked: {item.blockedReason}
                          </div>
                        ) : null}
                      </div>
                      {item.blocked ? (
                        <span title={item.blockedReason ?? 'Blocked'}>
                          <AlertTriangle className="size-4 text-warning" aria-hidden />
                        </span>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            )}
            {tab.items.length > PAGE_SIZE ? (
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={tab.items.length}
                onPageChange={(p) =>
                  setPageByTab((prev) => ({ ...prev, [tab.key]: p }))
                }
                className="mt-2 rounded-md border"
              />
            ) : null}
          </>
        )}
      </div>
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      // Why: long copy ("Each tab is one source. Tick the rows… ") clutters
      // the modal header. Tuck it behind an Info icon next to the title;
      // the tooltip surfaces it on hover for users who want the context,
      // and the visible header stays compact.
      title={
        description ? (
          <span className="inline-flex items-center gap-2">
            {title}
            <HelpTooltip text={description} />
          </span>
        ) : (
          title
        )
      }
      className="max-w-3xl"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {readOnly
              ? `${tabs.reduce((sum, t) => sum + t.items.length, 0)} pending`
              : `${totalSelected} selected${totalSelectable > 0 ? ` of ${totalSelectable}` : ''}`}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {!readOnly && tabs.length > 1 ? (
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-transparent px-2 py-1 text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground">
                <Checkbox
                  checked={
                    totalSelectable > 0 && totalSelected >= totalSelectable
                      ? true
                      : totalSelected > 0
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={() =>
                    allSelected ? clearAllGlobal() : selectAllGlobal()
                  }
                  disabled={totalSelectable === 0}
                  aria-label="Select every row across every tab"
                />
                <span className="font-medium">Select all tabs</span>
              </label>
            ) : null}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={posting}>
              Close
            </Button>
            {readOnly && onSwitchToPost ? (
              // Why: lets the user flip from "View pending" to the
              // selectable picker without closing the modal - they've
              // already seen what's pending, this is the natural next
              // step. Disabled when there's nothing to post.
              <Button
                type="button"
                onClick={onSwitchToPost}
                disabled={tabs.every((t) => t.items.every((i) => i.blocked))}
              >
                <Send className="size-4" /> Post Now
              </Button>
            ) : null}
            {!readOnly ? (
              <Button
                type="button"
                onClick={submit}
                disabled={totalSelected === 0 || !!posting}
              >
                {posting ? <Loader2 className="size-4 animate-spin" /> : null}
                Post {totalSelected > 0 ? `(${totalSelected})` : ''}
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner />
      ) : tabs.length === 0 ? (
        // Why: when the parent passes tabs=[] (e.g. right after a
        // successful post invalidates unposted-preview and the new data
        // hasn't arrived yet), renderTabContent(tabs[0]) would call
        // into `tab.key` on undefined. Show an empty placeholder
        // instead so the modal stays mounted and renders cleanly.
        <EmptyState
          title={readOnly ? 'Nothing pending' : 'Nothing to post'}
          description="There are no pending entries to show right now."
        />
      ) : isSingle ? (
        renderTabContent(tabs[0]!)
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap gap-1">
            {tabs.map((t) => (
              <TabsTrigger key={t.key} value={t.key} className="gap-2">
                {t.label}
                <Badge variant="secondary">{t.items.length}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-4">
              {renderTabContent(t)}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </Modal>
  )
}
